import { z } from "zod";

export const DemoCatalogEntrySchema = z.object({
  id: z.string().describe("ID"),
  display_name: z.string().describe("Display Name"),
  company: z.string().describe("Company"),
  business: z.string().describe("Business"),
  industry: z.string().describe("Industry"),
  scale_mode: z.string().describe("Scale Mode"),
  size_label: z.enum(["small", "medium", "large"]).describe("Size Label"),
  total_records: z.number().int().describe("Total Records"),
  record_counts: z
    .record(z.string(), z.number().int())
    .describe("Record Counts"),
  generated_at: z.string().describe("Generated At"),
  generator_version: z.string().describe("Generator Version"),
  tier_count: z.number().int().describe("Tier Count"),
  entity_types: z.number().int().describe("Entity Types"),
});
export type DemoCatalogEntry = z.infer<typeof DemoCatalogEntrySchema>;

export const DemoCatalogSchema = z.object({
  version: z.number().int().describe("Version"),
  generated_at: z.string().describe("Generated At"),
  datasets: z.array(DemoCatalogEntrySchema).describe("Datasets"),
});
export type DemoCatalog = z.infer<typeof DemoCatalogSchema>;

// A single entity's records within a demo dataset payload. `records` is left
// loosely typed (arbitrary objects) — the import pipeline validates each
// entity's shape downstream.
export const DemoDatasetFileSchema = z.object({
  entity: z.string().describe("Entity"),
  records: z.array(z.record(z.string(), z.unknown())).describe("Records"),
});
export type DemoDatasetFile = z.infer<typeof DemoDatasetFileSchema>;

// The flattened, unzipped demo dataset published to R2 at
// `demo-bundles/{dataset_id}/dataset.json` (ADV-1057). A JSON sibling of the
// `bundle.zip` so runtime services can fetch it without an unzip dependency.
// Keyed by the full dataset id (e.g. `canopy-turf-ci`) — scale included — so
// each catalog entry resolves to its own payload.
export const DemoDatasetSchema = z.object({
  version: z.number().int().describe("Version"),
  dataset_id: z.string().describe("Dataset ID"),
  generated_at: z.string().describe("Generated At"),
  files: z.array(DemoDatasetFileSchema).describe("Files"),
});
export type DemoDataset = z.infer<typeof DemoDatasetSchema>;

export const DemoImportRequestSchema = z.object({
  dataset_id: z.string().describe("Dataset ID"),
  reset_first: z.boolean().optional().default(false).describe("Reset First"),
});
export type DemoImportRequest = z.infer<typeof DemoImportRequestSchema>;

export const DemoImportStatusSchema = z.object({
  batch_id: z.string().uuid().describe("Batch ID"),
  dataset_id: z.string().describe("Dataset ID"),
  status: z
    .enum(["pending", "importing", "completed", "failed"])
    .describe("Status"),
  total_jobs: z.number().int().describe("Total Jobs"),
  completed_jobs: z.number().int().describe("Completed Jobs"),
  failed_jobs: z.number().int().describe("Failed Jobs"),
  total_records: z.number().int().describe("Total Records"),
  created_count: z.number().int().describe("Created"),
  updated_count: z.number().int().describe("Updated"),
  error_count: z.number().int().describe("Errors"),
  started_at: z.string().nullable().describe("Started At"),
  completed_at: z.string().nullable().describe("Completed At"),
});
export type DemoImportStatus = z.infer<typeof DemoImportStatusSchema>;
